import { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, CardContent } from '@klear/ui/components/card'
import { Progress } from '@klear/ui/components/progress'
import { useFunderAccounts } from '@klear/ui/hooks/use-funder-accounts'
import { useToast } from '@klear/ui/hooks/use-toast'
import { useCurrencyScopedReport } from '@klear/ui/hooks/use-currency-scoped-report'
import { trpc } from '@klear/ui/lib/trpc'
import { BasisPoints, MAX_BASIS_POINTS } from '@klear/basis-points'
import { Money } from '@klear/money'
import type { CurrencyCode } from '@klear/constants/currency'
import { CurrencyReportSelector } from '@klear/ui/components/currency-report-selector'
import { Button } from '@klear/ui/components/button'
import { CapitalReportExportModal } from '@/app/reports/capital-report-export-modal'
import {
  spvUtilizationFromWire,
  utilizationForCurrency,
  type SpvUtilizationByCurrency,
  type SpvUtilizationData
} from '@/app/reports/spv-utilization'
import { Loader2, RefreshCw, Download } from 'lucide-react'
import isError from 'lodash/isError'
import values from 'lodash/values'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

type FunderAccount = NonNullable<ReturnType<typeof useFunderAccounts>['funderAccounts']>[number]

type UtilizationEntry =
  | { status: 'loading' }
  | { status: 'ready'; data: SpvUtilizationData }
  | { status: 'error'; error: Error }

function isReadyUtilizationEntry(entry: UtilizationEntry): entry is Extract<UtilizationEntry, { status: 'ready' }> {
  return entry.status === 'ready'
}

const LOADING_UTILIZATION_ENTRY: UtilizationEntry = { status: 'loading' }

const SpvUtilizationCard = ({
  funder,
  utilizationEntry = LOADING_UTILIZATION_ENTRY,
  selectedCurrency
}: {
  funder: FunderAccount
  utilizationEntry?: UtilizationEntry
  selectedCurrency: CurrencyCode
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const readyUtilization = utilizationEntry.status === 'ready' ? utilizationEntry.data : null
  const currencyUtilization = utilizationForCurrency(readyUtilization, selectedCurrency)
  const utilization = currencyUtilization?.utilization ?? BasisPoints.ZERO

  const hasOutboundExposure =
    currencyUtilization != null && currencyUtilization.totalOutbound.greaterThan(currencyUtilization.totalOutbound.zero)
  const hasCash = currencyUtilization != null && currencyUtilization.totalInbound.isPositive()
  const isTreasuryCurrency = readyUtilization?.treasuryCurrency === selectedCurrency

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <div>
          <h2 className="text-2xl">{funder.organizationName}</h2>
          <p className="text-sm text-muted-foreground">SPV funder organization · {selectedCurrency}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setIsModalOpen(true)} className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Export Capital Report
        </Button>
      </div>
      <Card className="flex flex-col shadow-sm">
        <CardContent className="space-y-6 mt-6">
          {utilizationEntry.status === 'error' && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
              {utilizationEntry.error.message || 'Unable to load SPV utilization data for this funder.'}
            </div>
          )}
          {utilizationEntry.status === 'loading' && (
            <div className="rounded-2xl border border-dashed border-border bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
              Loading utilization metrics…
            </div>
          )}
          {readyUtilization && !currencyUtilization && (
            <div className="rounded-2xl border border-dashed border-border bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
              No {selectedCurrency} utilization for this SPV.
            </div>
          )}
          {currencyUtilization && (
            <div className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  {
                    label: 'Cash balance',
                    value: currencyUtilization.totalInbound.toString(),
                    helper: 'Klear account balance held for this SPV'
                  },
                  {
                    label: 'Committed',
                    value: currencyUtilization.committed.toString(),
                    helper: 'Accepted offers — promised but not yet paid out'
                  },
                  {
                    label: 'Available',
                    value: currencyUtilization.headroom.toString(),
                    helper: isTreasuryCurrency
                      ? 'Cash balance less commitments — what this SPV can still commit'
                      : `Funded from ${readyUtilization?.treasuryCurrency} cash, so this is FX exposure rather than a shortfall`
                  },
                  {
                    label: 'Capital at risk',
                    value: currencyUtilization.totalOutbound.toString(),
                    helper: 'Deployed principal not yet recovered (remaining plus in-flight)',
                    parts: [
                      { label: 'Invoice offers', value: currencyUtilization.invoiceOutbound.toString() },
                      { label: 'PaaS advances', value: currencyUtilization.paasOutbound.toString() }
                    ]
                  },
                  {
                    label: 'In transit',
                    value: currencyUtilization.inTransitInbound.toString(),
                    helper: 'Recovery payments processing or succeeded but not yet posted'
                  },
                  {
                    label: 'Total exposure',
                    value: currencyUtilization.totalAmount.toString(),
                    helper: 'Cash balance and capital at risk'
                  }
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="min-w-0 rounded-2xl border border-border bg-background p-4 shadow-sm"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
                      {stat.label}
                    </p>
                    <p className="mt-3 text-xl font-semibold tabular-nums leading-tight text-foreground sm:text-2xl">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">{stat.helper}</p>
                    {stat.parts != null && (
                      <dl className="mt-3 space-y-1 border-t border-border pt-2">
                        {stat.parts.map((part) => (
                          <div key={part.label} className="flex items-baseline justify-between gap-2 text-xs">
                            <dt className="min-w-0 truncate text-muted-foreground">{part.label}</dt>
                            <dd className="font-medium tabular-nums text-foreground">{part.value}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                ))}
              </div>
              <div className="space-y-3 rounded-2xl border border-border bg-muted/50 p-4">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>Utilization</span>
                  <span className="font-semibold text-foreground">{utilization.toString({ digits: 1 })}</span>
                </div>
                <Progress value={utilization.value} max={MAX_BASIS_POINTS} className="h-2 rounded-full" />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {hasOutboundExposure
                    ? 'Share of SPV capital at risk, net of posted recoveries.'
                    : hasCash
                      ? 'No exposure yet — this SPV is holding cash.'
                      : 'No exposure and no cash held in this currency.'}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CapitalReportExportModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        funderId={funder.id}
        funderOrganizationName={funder.organizationName}
      />
    </div>
  )
}

const SpvUtlizationReport = () => {
  useDocumentTitle('SPV Utilization')
  const { funderAccounts, isLoading } = useFunderAccounts()
  const { toast } = useToast()
  const utils = trpc.useUtils()
  const syncStripePayments = trpc.payment.syncStripePayments.useMutation()
  const [utilizationState, setUtilizationState] = useState<Record<number, UtilizationEntry>>({})
  const [isRefreshing, setIsRefreshing] = useState(false)

  const funderOrganizationIds = useMemo(
    () =>
      funderAccounts
        ?.map((f) => f.organizationId)
        .sort()
        .join(',') ?? '',
    [funderAccounts]
  )

  const fetchUtilization = useCallback(async () => {
    if (!funderAccounts?.length) {
      setUtilizationState({})
      return
    }

    setUtilizationState(
      funderAccounts.reduce<Record<number, UtilizationEntry>>((acc, funder) => {
        acc[funder.organizationId] = { status: 'loading' }
        return acc
      }, {})
    )

    await Promise.all(
      funderAccounts.map(async (funder) => {
        try {
          const data = spvUtilizationFromWire(
            await utils.client.klearAccount.getUtilization.query({
              organizationId: funder.organizationId
            })
          )
          setUtilizationState((prev) => ({
            ...prev,
            [funder.organizationId]: { status: 'ready', data }
          }))
        } catch (error) {
          setUtilizationState((prev) => ({
            ...prev,
            [funder.organizationId]: {
              status: 'error',
              error: isError(error) ? error : new Error('Failed to load utilization')
            }
          }))
        }
      })
    )
  }, [funderAccounts, utils.client.klearAccount.getUtilization])

  useEffect(() => {
    void fetchUtilization()
  }, [funderOrganizationIds])

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    try {
      const { paymentsUpdated } = await syncStripePayments.mutateAsync({})
      await fetchUtilization()
      toast({
        title: 'SPV utilization refreshed',
        description:
          paymentsUpdated > 0
            ? `Synced ${paymentsUpdated} payment${paymentsUpdated === 1 ? '' : 's'} from Stripe.`
            : 'Payment statuses are up to date.'
      })
    } catch (error) {
      const message = isError(error) ? error.message : 'Could not sync payments from Stripe.'
      toast({
        title: 'Refresh failed',
        description: message,
        variant: 'destructive'
      })
    } finally {
      setIsRefreshing(false)
    }
  }, [fetchUtilization, syncStripePayments, toast])

  const entries = useMemo(() => values(utilizationState), [utilizationState])
  const loadedEntries = entries.filter(isReadyUtilizationEntry)

  const utilizationMonies = useMemo(
    () =>
      loadedEntries.flatMap((entry) =>
        entry.data.byCurrency.flatMap((row) => [
          row.totalInbound,
          row.inTransitInbound,
          row.totalOutbound,
          row.totalAmount,
          row.committed,
          row.draftPipeline
        ])
      ),
    [loadedEntries]
  )

  const { selectedCurrency, setSelectedCurrency, showCurrencySelector, availableCurrencies } = useCurrencyScopedReport([
    utilizationMonies
  ])

  const aggregatedTotals = useMemo(() => {
    const zero = Money.zero(selectedCurrency)

    if (loadedEntries.length === 0) {
      return {
        totalInbound: zero,
        inTransitInbound: zero,
        totalOutbound: zero,
        totalAmount: zero,
        committed: zero,
        headroom: zero,
        utilization: BasisPoints.ZERO,
        loadedCount: 0,
        reportingCount: 0
      }
    }

    const buckets = loadedEntries
      .map((entry) => utilizationForCurrency(entry.data, selectedCurrency))
      .filter((row): row is SpvUtilizationByCurrency => row != null)

    if (buckets.length === 0) {
      return {
        totalInbound: zero,
        inTransitInbound: zero,
        totalOutbound: zero,
        totalAmount: zero,
        committed: zero,
        headroom: zero,
        utilization: BasisPoints.ZERO,
        loadedCount: loadedEntries.length,
        reportingCount: 0
      }
    }

    const totalInbound = Money.sum(buckets.map((row) => row.totalInbound))
    const inTransitInbound = Money.sum(buckets.map((row) => row.inTransitInbound))
    const totalOutbound = Money.sum(buckets.map((row) => row.totalOutbound))
    const totalAmount = Money.sum(buckets.map((row) => row.totalAmount))
    const utilization = BasisPoints.fromMoney(totalOutbound, totalAmount)

    return {
      totalInbound,
      inTransitInbound,
      totalOutbound,
      totalAmount,
      committed: Money.sum(buckets.map((row) => row.committed)),
      headroom: Money.sum(buckets.map((row) => row.headroom)),
      utilization,
      loadedCount: loadedEntries.length,
      reportingCount: buckets.length
    }
  }, [loadedEntries, selectedCurrency])

  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-background dark:via-background dark:to-background">
        <div className="flex h-full flex-col items-center justify-center text-sm text-muted-foreground">
          Loading funder accounts…
        </div>
      </div>
    )
  }

  if (!funderAccounts?.length) {
    return (
      <div className="bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-background dark:via-background dark:to-background">
        <div className="flex h-full flex-col items-center justify-center text-sm text-muted-foreground">
          No SPV funders available right now.
        </div>
      </div>
    )
  }

  const totalHelperText =
    aggregatedTotals.loadedCount > 0
      ? `${aggregatedTotals.reportingCount}/${aggregatedTotals.loadedCount} SPVs reporting ${selectedCurrency}`
      : `${funderAccounts.length} SPVs loading`

  const exposureHelperText =
    aggregatedTotals.reportingCount > 0
      ? `Utilization ${aggregatedTotals.utilization.toString({ digits: 1 })}`
      : totalHelperText

  const totalStats = [
    {
      label: 'Total cash balance',
      value: aggregatedTotals.totalInbound.toString(),
      helper: totalHelperText
    },
    {
      label: 'Total committed',
      value: aggregatedTotals.committed.toString(),
      helper: totalHelperText
    },
    {
      label: 'Total available',
      value: aggregatedTotals.headroom.toString(),
      helper: totalHelperText
    },
    {
      label: 'Total capital at risk',
      value: aggregatedTotals.totalOutbound.toString(),
      helper: totalHelperText
    },
    {
      label: 'Total in transit',
      value: aggregatedTotals.inTransitInbound.toString(),
      helper: totalHelperText
    },
    {
      label: 'Total SPV exposure',
      value: aggregatedTotals.totalAmount.toString(),
      helper: exposureHelperText
    }
  ]

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-background dark:via-background dark:to-background">
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-10 md:px-8">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1 text-muted-foreground">
            <p className="text-xs uppercase tracking-[0.4em] text-muted-foreground">Capital Insights</p>
            <h1 className="text-3xl font-bold text-foreground">SPV Utilization</h1>
            <p className="text-sm text-muted-foreground">
              Every SPV funder has its own card so you can scan capital usage without switching views.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {showCurrencySelector && (
              <CurrencyReportSelector
                value={selectedCurrency}
                onChange={setSelectedCurrency}
                currencies={availableCurrencies}
                className="h-9 w-[140px] text-sm"
              />
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void handleRefresh()}
              disabled={isRefreshing || !funderAccounts?.length}
              className="shrink-0"
            >
              {isRefreshing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Refresh
            </Button>
          </div>
        </header>
        <div className="space-y-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {totalStats.map((stat) => (
              <Card key={stat.label} className="">
                <CardContent className="min-w-0 space-y-2 p-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
                    {stat.label}
                  </p>
                  <p className="text-2xl font-semibold tabular-nums leading-tight text-foreground xl:text-3xl">
                    {stat.value}
                  </p>
                  <p className="text-sm text-muted-foreground">{stat.helper}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          {funderAccounts.map((funder) => (
            <SpvUtilizationCard
              key={funder.organizationId}
              funder={funder}
              utilizationEntry={utilizationState[funder.organizationId]}
              selectedCurrency={selectedCurrency}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default SpvUtlizationReport
